import type { VoucherFormValues } from './voucher-form-schema'
import type { IVoucher } from '@/entities/voucher'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import { usePoiCategoriesQuery, useTenantPoisInfiniteQuery } from '@/entities/poi'
import { useTenantLanguages } from '@/entities/tenant'
import { useTenantDestinationsQuery } from '@/entities/tenant-destination'
import { useToursQuery } from '@/entities/tour'
import { AdminScopeType } from '@/shared/constants/admin-scope'
import { DEFAULT_FETCH_LIMIT } from '@/shared/constants/pagination'
import { useLanguageFilter } from '@/shared/hooks/use-language-filter'
import { Button } from '@/shared/ui/button'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'
import { VoucherApplicableSection } from './voucher-applicable-section'
import { VoucherBasicSection } from './voucher-basic-section'
import { voucherFormSchema } from './voucher-form-schema'
import { VoucherStatsSection } from './voucher-stats-section'

export type { VoucherFormValues } from './voucher-form-schema'

const ALL = '__all__'

interface VoucherFormProps {
  tenantId: string
  defaultValues?: Partial<VoucherFormValues>
  voucher?: IVoucher | undefined
  isSubmitting: boolean
  onSubmit: (values: VoucherFormValues) => Promise<void>
  onCancel: () => void
  mode: 'create' | 'edit'
}

function getLocalizedName(
  localizedData: Array<{ language: string; name: string }>,
  languageCode: string,
  fallback: string,
): string {
  return (
    localizedData.find((t) => t.language === languageCode)?.name ??
    localizedData[0]?.name ??
    fallback
  )
}

export function VoucherForm({
  tenantId,
  defaultValues,
  voucher,
  isSubmitting,
  onSubmit,
  onCancel,
  mode,
}: VoucherFormProps) {
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<VoucherFormValues>({
    resolver: zodResolver(voucherFormSchema),
    defaultValues: {
      code: '',
      name: '',
      discountPercent: 10,
      quantity: 100,
      startDate: '',
      endDate: '',
      status: 'AVAILABLE',
      type: 'TOUR',
      applicableTourIds: [],
      applicablePoiIds: [],
      ...defaultValues,
    },
  })

  const selectedType = watch('type')

  // --- Language ---
  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const { selectedLanguageCode, setSelectedLanguageCode, languageOptions, defaultLanguageCode } =
    useLanguageFilter(tenantLanguagesQuery.items, tenantId)
  const effectiveLanguageCode = selectedLanguageCode || defaultLanguageCode

  // --- Destinations (shared by POI & Tour) ---
  const destinationsQuery = useTenantDestinationsQuery({
    current: 1,
    pageSize: DEFAULT_FETCH_LIMIT,
    ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
    isActive: true,
  })
  const destinationOptions = useMemo(
    () =>
      (destinationsQuery.data?.data?.data ?? []).map((d) => ({
        label: Object.values(d.localizedData ?? {})[0]?.name ?? d.id,
        value: d.id,
      })),
    [destinationsQuery.data],
  )

  // --- POI filters ---
  const [poiSearch, setPoiSearch] = useState('')
  const [poiCategory, setPoiCategory] = useState('')
  const [poiStatus, setPoiStatus] = useState<'active' | 'inactive' | typeof ALL>(ALL)
  const [poiCountryId, setPoiCountryId] = useState('')
  const [poiProvinceId, setPoiProvinceId] = useState('')
  const [poiDestinationId, setPoiDestinationId] = useState('')

  const poisQuery = useTenantPoisInfiniteQuery(tenantId, {
    pageSize: 20,
    ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
    ...(poiCategory ? { category: poiCategory } : {}),
    ...(poiProvinceId ? { provinceId: poiProvinceId } : {}),
    ...(poiDestinationId ? { tenantDestinationId: poiDestinationId } : {}),
    isActive: poiStatus === 'active' ? true : poiStatus === 'inactive' ? false : undefined,
  })
  const allPois = useMemo(
    () => poisQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [poisQuery.data],
  )
  const poiItems = useMemo(
    () =>
      allPois
        .map((poi) => ({
          id: poi.id,
          label: getLocalizedName(poi.localizedData, effectiveLanguageCode, poi.id),
        }))
        .filter(
          (item) => !poiSearch.trim() || item.label.toLowerCase().includes(poiSearch.toLowerCase()),
        ),
    [allPois, effectiveLanguageCode, poiSearch],
  )

  const poiCategoriesQuery = usePoiCategoriesQuery(tenantId, {
    ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
  })
  const categoryOptions = useMemo(() => {
    const opts = new Map<string, { label: string; value: string }>()
    for (const item of poiCategoriesQuery.data?.data ?? []) {
      const value = item.id.trim()
      if (!value) {
        continue
      }
      const label = resolvePoiCategoryName(
        item,
        [effectiveLanguageCode, defaultLanguageCode].filter(Boolean),
      )
      opts.set(value, { value, label: label || value })
    }
    return Array.from(opts.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [poiCategoriesQuery.data, effectiveLanguageCode, defaultLanguageCode])

  const countriesQuery = useCountriesQuery({})
  const countryItems = countriesQuery.data?.data ?? []
  const countryOptions = countryItems.map((c) => ({
    label: c.nativeName ?? c.name ?? c.code,
    value: c.id,
  }))

  const provincesQuery = useProvincesQuery({ countryId: poiCountryId })
  const provinceItems = provincesQuery.data?.data ?? []
  const provinceOptions = provinceItems.map((p) => ({
    label: p.nativeName ?? p.name ?? p.code,
    value: p.id,
  }))

  const languageIsNotDefault = selectedLanguageCode && selectedLanguageCode !== defaultLanguageCode
  const poiActiveFilterCount = [
    languageIsNotDefault ? selectedLanguageCode : '',
    poiCategory,
    poiCountryId,
    poiProvinceId,
    poiDestinationId,
    poiStatus !== ALL ? poiStatus : '',
  ].filter(Boolean).length

  function resetPoiFilters() {
    setSelectedLanguageCode(defaultLanguageCode)
    setPoiCategory('')
    setPoiCountryId('')
    setPoiProvinceId('')
    setPoiDestinationId('')
    setPoiStatus(ALL)
  }

  // --- Tour filters ---
  const [tourSearch, setTourSearch] = useState('')
  const [tourDestinationId, setTourDestinationId] = useState('')
  const [tourStatus, setTourStatus] = useState<'active' | 'inactive' | typeof ALL>(ALL)

  const toursQuery = useToursQuery(
    tenantId,
    {
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      ...(tourDestinationId ? { tenantDestinationId: tourDestinationId } : {}),
      ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
      isActive: tourStatus === 'active' ? true : tourStatus === 'inactive' ? false : undefined,
    },
    { scope: AdminScopeType.Tenant },
  )
  const tourItems = useMemo(
    () =>
      (toursQuery.data?.data ?? [])
        .map((tour) => ({
          id: tour.id,
          label: getLocalizedName(tour.localizedData, effectiveLanguageCode, tour.id),
        }))
        .filter(
          (item) =>
            !tourSearch.trim() || item.label.toLowerCase().includes(tourSearch.toLowerCase()),
        ),
    [toursQuery.data, effectiveLanguageCode, tourSearch],
  )

  const tourActiveFilterCount = [
    languageIsNotDefault ? selectedLanguageCode : '',
    tourDestinationId,
    tourStatus !== ALL ? tourStatus : '',
  ].filter(Boolean).length

  function resetTourFilters() {
    setSelectedLanguageCode(defaultLanguageCode)
    setTourDestinationId('')
    setTourStatus(ALL)
  }

  const applicableError = errors.applicableTourIds?.message ?? errors.applicablePoiIds?.message

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {mode === 'edit' && voucher && <VoucherStatsSection voucher={voucher} />}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(460px,1.15fr)]">
        <VoucherBasicSection control={control} errors={errors} mode={mode} />
        <VoucherApplicableSection
          control={control}
          selectedType={selectedType}
          applicableError={applicableError}
          languageOptions={languageOptions}
          effectiveLanguageCode={effectiveLanguageCode}
          setSelectedLanguageCode={setSelectedLanguageCode}
          destinationOptions={destinationOptions}
          tourSearch={tourSearch}
          setTourSearch={setTourSearch}
          tourDestinationId={tourDestinationId}
          setTourDestinationId={setTourDestinationId}
          tourStatus={tourStatus}
          setTourStatus={setTourStatus}
          tourActiveFilterCount={tourActiveFilterCount}
          resetTourFilters={resetTourFilters}
          toursLoading={toursQuery.isLoading}
          tourItems={tourItems}
          poiSearch={poiSearch}
          setPoiSearch={setPoiSearch}
          poiCountryId={poiCountryId}
          setPoiCountryId={setPoiCountryId}
          poiProvinceId={poiProvinceId}
          setPoiProvinceId={setPoiProvinceId}
          poiDestinationId={poiDestinationId}
          setPoiDestinationId={setPoiDestinationId}
          poiCategory={poiCategory}
          setPoiCategory={setPoiCategory}
          poiStatus={poiStatus}
          setPoiStatus={setPoiStatus}
          poiActiveFilterCount={poiActiveFilterCount}
          resetPoiFilters={resetPoiFilters}
          poisLoading={poisQuery.isLoading}
          poiItems={poiItems}
          fetchNextPoiPage={poisQuery.fetchNextPage}
          hasNextPoiPage={Boolean(poisQuery.hasNextPage)}
          isFetchingNextPoiPage={poisQuery.isFetchingNextPage}
          countryOptions={countryOptions}
          provinceOptions={provinceOptions}
          categoryOptions={categoryOptions}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? mode === 'create'
              ? t('common.creating')
              : t('common.saving')
            : mode === 'create'
              ? t('common.create')
              : t('common.save')}
        </Button>
      </div>
    </form>
  )
}
